import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { PdpSharedSimilarSoldItemsColumn } from './PdpSharedSimilarSoldItemsColumn.tsx';
import { useDetailEntries } from './useDetailEntries.ts';
import { useAppSelector } from '../hooks/useReactReduxHooks.ts';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';
import { useShouldShowSimilarSoldItems } from '../hooks/useShouldShowSimilarSoldItems.ts';

import classnames from 'classnames';
import dibsCss from 'dibs-css';

import { type PdpSharedSimilarSoldItemsColumns_item$key } from './__generated__/PdpSharedSimilarSoldItemsColumns_item.graphql.ts';
import { type PdpSharedSimilarSoldItemsColumns_viewer$key } from './__generated__/PdpSharedSimilarSoldItemsColumns_viewer.graphql.ts';
import { type PdpSharedSimilarSoldItemsColumn_user$key } from './__generated__/PdpSharedSimilarSoldItemsColumn_user.graphql.ts';

const itemFragment = graphql`
    fragment PdpSharedSimilarSoldItemsColumns_item on Item {
        ...PdpSharedSimilarSoldItemsColumn_item
            @arguments(
                userId: $userId
                isTrade: $isTrade
                userCountryCode: $userCountryCode
                priceBookName: $priceBookName
                catchNonFatalErrors: false
            )
        ...useDetailEntries_item
        ...useShouldShowSimilarSoldItems_item
    }
`;

const viewerFragment = graphql`
    fragment PdpSharedSimilarSoldItemsColumns_viewer on Viewer {
        ...useShouldShowSimilarSoldItems_viewer
        ...PdpSharedSimilarSoldItemsColumn_viewer
        similarSoldItemsConnection(itemId: $itemId, first: 4) {
            edges {
                node {
                    itemId
                    soldDate
                    item {
                        ...PdpSharedSimilarSoldItemsColumn_item
                            @arguments(
                                userId: $userId
                                isTrade: $isTrade
                                userCountryCode: $userCountryCode
                                priceBookName: $priceBookName
                                catchNonFatalErrors: false
                            )
                    }
                }
            }
        }
    }
`;

type PdpSharedSimilarSoldItemsColumnsProps = {
    item: PdpSharedSimilarSoldItemsColumns_item$key;
    viewer: PdpSharedSimilarSoldItemsColumns_viewer$key;
    user?: PdpSharedSimilarSoldItemsColumn_user$key | null;
    currency: string;
    className?: string;
    inModal?: boolean;
};

export const PdpSharedSimilarSoldItemsColumns: FC<PdpSharedSimilarSoldItemsColumnsProps> = ({
    item: itemRef,
    viewer: viewerRef,
    user,
    currency,
    className,
    inModal,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const { afterDisplay } = useAppSelector(relay => relay?.relayVariables?.variables || {});
    const { isBelowMinPriceEstimate } = useShouldShowSimilarSoldItems({
        viewer,
        item,
        currency,
        afterDisplay,
    });
    const detailEntries = useDetailEntries({ item });
    const similarSoldItems =
        viewer?.similarSoldItemsConnection?.edges
            ?.map(edge => edge?.node || null)
            ?.filter(filterNulls) ?? [];

    return (
        <div className={classnames(dibsCss.flex, className)}>
            <PdpSharedSimilarSoldItemsColumn
                viewer={viewer}
                item={item}
                user={user}
                isCurrentItem
                currency={currency}
                isBelowEstimate={isBelowMinPriceEstimate}
                detailEntries={detailEntries}
                inModal={inModal}
            />
            {similarSoldItems.map(({ itemId, item: similarSoldItem, soldDate }, index) => (
                <PdpSharedSimilarSoldItemsColumn
                    key={itemId}
                    viewer={viewer}
                    item={similarSoldItem}
                    user={user}
                    itemSoldDate={soldDate}
                    currency={currency}
                    detailEntries={detailEntries}
                    isLastItem={index === similarSoldItems.length - 1}
                />
            ))}
        </div>
    );
};
